import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getTopClips } from '../../services/HighlightsService';
import { getLatestSeason, getDivisionsBySeason } from '../../services/MatchService';
import Loading from '../../components/Loading';
import { HighlightClip } from '../../models/HighlightClip';
import { track } from '../../utils/track';
import '../../pages/PlayerPages/PlayerHighlights/PlayerHighlights.css';
import './TopClipsPage.css';

import inferno from '../../content/maps/de_inferno.jpg';
import mirage from '../../content/maps/de_mirage.jpg';
import nuke from '../../content/maps/de_nuke.jpg';
import overpass from '../../content/maps/de_overpass.jpg';
import ancient from '../../content/maps/de_ancient.jpg';
import vertigo from '../../content/maps/de_vertigo.jpg';
import anubis from '../../content/maps/de_anubis.jpg';
import dust2 from '../../content/maps/de_dust2.jpg';
import train from '../../content/maps/de_train.jpg';
import cache from '../../content/maps/de_cache.jpg';

const VOLUME_STORAGE_KEY = 'powerstats-clip-volume';
const MUTED_STORAGE_KEY = 'powerstats-clip-muted';

const mapImages: Record<string, string> = {
    'de_inferno': inferno, 'de_mirage': mirage, 'de_nuke': nuke, 'de_overpass': overpass,
    'de_vertigo': vertigo, 'de_ancient': ancient, 'de_anubis': anubis, 'de_dust2': dust2, 'de_train': train, 'de_cache': cache,
    'inferno': inferno, 'mirage': mirage, 'nuke': nuke, 'overpass': overpass,
    'vertigo': vertigo, 'ancient': ancient, 'anubis': anubis, 'dust2': dust2, 'train': train, 'cache': cache
};

const getMapImage = (mapName?: string | null) => mapImages[mapName?.toLowerCase() || ''] || dust2;

const formatMapName = (mapName: string | undefined | null): string => {
    if (!mapName) return 'Unknown';
    const cleanName = mapName.replace('de_', '');
    return cleanName.charAt(0).toUpperCase() + cleanName.slice(1);
};

const isClutchType = (type: string | undefined): boolean => {
    if (!type) return false;
    return type.toLowerCase().startsWith('clutch');
};

const getClutchLabel = (type: string): string => {
    const lower = type.toLowerCase();
    if (lower === 'clutch1v2') return '1v2';
    if (lower === 'clutch1v3') return '1v3';
    if (lower === 'clutch1v4') return '1v4';
    if (lower === 'clutch1v5') return '1v5';
    if (lower === 'clutch2v4') return '2v4';
    if (lower === 'clutch2v5') return '2v5';
    return 'Clutch';
};

const KillBadge: React.FC<{ kills: number; highlightType?: string }> = ({ kills, highlightType }) => {
    const isClutch = isClutchType(highlightType);
    const is2vX = highlightType?.toLowerCase().startsWith('clutch2v');

    const getBadgeClass = () => {
        if (is2vX) return 'kill-badge clutch-2vx';
        if (isClutch) return 'kill-badge clutch';
        if (kills >= 5) return 'kill-badge ace';
        if (kills >= 4) return 'kill-badge quad';
        if (kills >= 3) return 'kill-badge triple';
        return 'kill-badge';
    };

    const getLabel = () => {
        if (isClutch && highlightType) return getClutchLabel(highlightType);
        if (kills >= 5) return 'ACE';
        return `${kills}K`;
    };

    return <span className={getBadgeClass()}>{getLabel()}</span>;
};

const TopClipsPage: React.FC = () => {
    const [clips, setClips] = useState<HighlightClip[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedClip, setSelectedClip] = useState<HighlightClip | null>(null);
    const [season, setSeason] = useState<number | undefined>(undefined);
    const [division, setDivision] = useState<string>('all');
    const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);
    const [availableDivisions, setAvailableDivisions] = useState<string[]>([]);

    const videoRef = useRef<HTMLVideoElement>(null);

    const getSavedVolume = useCallback(() => {
        const saved = localStorage.getItem(VOLUME_STORAGE_KEY);
        return saved !== null ? parseFloat(saved) : 1;
    }, []);

    const getSavedMuted = useCallback(() => {
        const saved = localStorage.getItem(MUTED_STORAGE_KEY);
        return saved === 'true';
    }, []);

    const handleVideoLoaded = useCallback(() => {
        if (videoRef.current) {
            videoRef.current.volume = getSavedVolume();
            videoRef.current.muted = getSavedMuted();
        }
    }, [getSavedVolume, getSavedMuted]);

    const handleVolumeChange = useCallback(() => {
        if (videoRef.current) {
            localStorage.setItem(VOLUME_STORAGE_KEY, videoRef.current.volume.toString());
            localStorage.setItem(MUTED_STORAGE_KEY, videoRef.current.muted.toString());
        }
    }, []);

    // Load seasons on mount
    useEffect(() => {
        const fetchSeasons = async () => {
            try {
                const latestSeason = await getLatestSeason();
                setSeason(latestSeason);
                const seasons = [];
                for (let s = 30; s <= latestSeason; s++) {
                    seasons.push(s);
                }
                setAvailableSeasons(seasons.reverse());
            } catch (error) {
                console.error('Error fetching seasons:', error);
            }
        };
        fetchSeasons();
    }, []);

    // Load divisions when season changes
    useEffect(() => {
        if (!season) return;
        const fetchDivisions = async () => {
            try {
                const divisions = await getDivisionsBySeason(season);
                const divisionOptions: string[] = ['all'];
                if (divisions.some((d: string) => d === 'ligaen')) divisionOptions.push('ligaen');
                if (divisions.some((d: string) => d === '1div')) divisionOptions.push('1div');
                if (divisions.some((d: string) => d.startsWith('2div'))) divisionOptions.push('2div');
                if (divisions.some((d: string) => d.startsWith('3div'))) divisionOptions.push('3div');
                if (divisions.some((d: string) => d.startsWith('4div'))) divisionOptions.push('4div');
                setAvailableDivisions(divisionOptions);
            } catch (error) {
                console.error('Error fetching divisions:', error);
            }
        };
        fetchDivisions();
    }, [season]);

    // Load clips when filters change
    useEffect(() => {
        if (!season) return;
        const fetchClips = async () => {
            setLoading(true);
            try {
                const result = await getTopClips(season, division);
                setClips(result);
                if (result.length > 0) {
                    setSelectedClip(result[0]);
                } else {
                    setSelectedClip(null);
                }
            } catch (error) {
                console.error('Error fetching top clips:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchClips();
    }, [season, division]);

    const selectedIndex = useMemo(() => {
        if (!selectedClip) return -1;
        return clips.findIndex(c => c.id === selectedClip.id);
    }, [clips, selectedClip]);

    const handleVideoEnded = useCallback(() => {
        if (clips.length === 0) return;
        const nextIndex = (selectedIndex + 1) % clips.length;
        setSelectedClip(clips[nextIndex]);
    }, [clips, selectedIndex]);

    const handleVideoPlay = useCallback(() => {
        if (selectedClip) {
            track('clip-play', {
                source: 'top',
                clipId: selectedClip.id,
                type: selectedClip.highlightType,
                map: selectedClip.mapName,
                season,
                division,
            });
        }
    }, [selectedClip, season, division]);

    if (loading && clips.length === 0) {
        return (
            <div className="top-clips-page">
                <h2 className="top-clips-title">Top Clips</h2>
                <Loading />
            </div>
        );
    }

    return (
        <div className="top-clips-page">
            <h2 className="top-clips-title">Top Clips</h2>

            {/* Filters */}
            <div className="top-clips-filters">
                <div className="filter-group">
                    <label>Sæson</label>
                    <select
                        value={season || ''}
                        onChange={(e) => {
                            setSeason(Number(e.target.value));
                            setDivision('all');
                        }}
                        className="filter-select"
                    >
                        {availableSeasons.map(s => (
                            <option key={s} value={s}>Sæson {s}</option>
                        ))}
                    </select>
                </div>
                <div className="filter-group">
                    <label>Division</label>
                    <select
                        value={division}
                        onChange={(e) => setDivision(e.target.value)}
                        className="filter-select"
                    >
                        {availableDivisions.map(d => (
                            <option key={d} value={d}>
                                {d === 'all' ? 'Alle divisioner'
                                    : d === 'ligaen' ? 'Ligaen'
                                    : d === '1div' ? '1. Division'
                                    : d === '2div' ? '2. Division'
                                    : d === '3div' ? '3. Division'
                                    : d === '4div' ? '4. Division'
                                    : d}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {clips.length === 0 ? (
                <div className="top-clips-empty">
                    <p>Ingen clips fundet for de valgte filtre</p>
                </div>
            ) : (
                <div className="top-clips-content">
                    {/* Main video player */}
                    <div className="top-clips-main">
                        {selectedClip && (
                            <div className="main-player">
                                <div className="video-container">
                                    {selectedClip.clipUrl ? (
                                        <video
                                            ref={videoRef}
                                            key={selectedClip.id}
                                            controls
                                            autoPlay
                                            playsInline
                                            src={selectedClip.clipUrl}
                                            poster={selectedClip.thumbnailUrl || undefined}
                                            onLoadedData={handleVideoLoaded}
                                            onVolumeChange={handleVolumeChange}
                                            onEnded={handleVideoEnded}
                                            onPlay={handleVideoPlay}
                                        >
                                            Din browser understøtter ikke video afspilning.
                                        </video>
                                    ) : (
                                        <div className="video-placeholder">
                                            <p>Video ikke tilgængelig</p>
                                        </div>
                                    )}
                                </div>
                                <div className="main-player-info">
                                    <div className="main-player-details">
                                        <div className="main-clip-rank-row">
                                            <span className="top-clip-rank">#{selectedIndex + 1}</span>
                                            {selectedClip.playerName && (
                                                <Link to={`/player/${selectedClip.steamId}`} className="top-clip-player-link">
                                                    {selectedClip.playerName}
                                                </Link>
                                            )}
                                        </div>
                                        {(selectedClip.team1Logo || selectedClip.team2Logo) && (
                                            <div className="main-match-header">
                                                <div className="main-team">
                                                    {selectedClip.team1Logo && <img src={selectedClip.team1Logo} alt="" className="main-team-logo" />}
                                                    <span className="main-team-name">{selectedClip.team1Name}</span>
                                                </div>
                                                <span className="main-vs">vs</span>
                                                <div className="main-team">
                                                    <span className="main-team-name">{selectedClip.team2Name}</span>
                                                    {selectedClip.team2Logo && <img src={selectedClip.team2Logo} alt="" className="main-team-logo" />}
                                                </div>
                                            </div>
                                        )}
                                        <div className="main-player-title">
                                            <KillBadge kills={selectedClip.killCount} highlightType={selectedClip.highlightType} />
                                            {isClutchType(selectedClip.highlightType) && (
                                                <span className="clutch-kills">{selectedClip.killCount}K</span>
                                            )}
                                            <span className="main-map-name">{formatMapName(selectedClip.mapName)}</span>
                                            <span className="round-info">Runde {selectedClip.roundNr}</span>
                                        </div>
                                    </div>
                                    <div className="main-player-actions">
                                        {selectedClip.clipUrl && (
                                            <button
                                                className="action-btn"
                                                title="Del"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(selectedClip.clipUrl);
                                                }}
                                            >
                                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                                                </svg>
                                            </button>
                                        )}
                                        {selectedClip.clipUrl && (
                                            <a href={selectedClip.clipUrl} download className="action-btn" title="Download">
                                                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                                                    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                                                </svg>
                                            </a>
                                        )}
                                        {selectedClip.matchId && (
                                            <Link to={`/match/${selectedClip.matchId}`} className="action-btn match-link" title="Gå til kamp">
                                                Gå til kamp
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="top-clips-sidebar">
                        <div className="sidebar-header">
                            <span>Top {clips.length} Clips</span>
                        </div>
                        <div className="clips-list">
                            {clips.map((clip, index) => (
                                <div
                                    key={clip.id}
                                    className={`clip-list-item ${selectedClip?.id === clip.id ? 'selected' : ''}`}
                                    onClick={() => setSelectedClip(clip)}
                                >
                                    <span className="sidebar-rank">#{index + 1}</span>
                                    <div className="clip-list-thumb">
                                        {clip.thumbnailUrl ? (
                                            <img src={clip.thumbnailUrl} alt="" />
                                        ) : (
                                            <img src={getMapImage(clip.mapName)} alt="" className="map-fallback" />
                                        )}
                                    </div>
                                    <div className="clip-list-info">
                                        <div className="clip-list-title">
                                            <KillBadge kills={clip.killCount} highlightType={clip.highlightType} />
                                            <span>{formatMapName(clip.mapName)}</span>
                                        </div>
                                        <div className="clip-list-meta">
                                            {clip.playerName && (
                                                <span className="sidebar-player-name">{clip.playerName}</span>
                                            )}
                                            {(clip.team1Logo || clip.team2Logo) && (
                                                <span className="clip-list-teams-logos">
                                                    {clip.team1Logo && <img src={clip.team1Logo} alt="" className="clip-mini-logo" />}
                                                    <span className="clip-vs-mini">vs</span>
                                                    {clip.team2Logo && <img src={clip.team2Logo} alt="" className="clip-mini-logo" />}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TopClipsPage;
